import React, { useEffect, useState, useMemo } from "react";
import { themes, optionalAvathar } from "services/constants";
import { useForm } from "react-hook-form";
import * as Icons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { CustomButton } from "components/CustomComponents/CustomButton";
import sessionHandling from "services/utils/notificationUtils";
import { customAPI } from "services/utils/CustomAPI";
import PropTypes from "prop-types";
import { Close } from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import { formKeyText, tooltipStyleProps } from "services/constants/Styles";

export const EditDepartment = ({
  setOpenEditScreen,
  editDepartmentDetails,
  setDepartmentApi,
}) => {
  const { domain, token, userDetails } = useSelector(
    (state) => state.tokenReducer
  );
  const [departmentLeadName, setDepartmentLeadName] = useState(
    editDepartmentDetails.department_lead_name
  );
  const [departmentProxyName, setDepartmentProxyName] = useState(
    editDepartmentDetails.proxy_name
  );
  const [departmentName, setDepartmentName] = useState(
    editDepartmentDetails.department_name
  );
  const { result: membersResult } = useFetch(`${domain}all-members/`);
  const departmentLeadList = useMemo(
    () => (Array.isArray(membersResult) ? membersResult : []),
    [membersResult]
  );
  const [editValue, setEditValue] = useState(true);
  const [buttonLoader, setButtonLoader] = useState(false);

  const {
    setValue,
    handleSubmit,
    reset,
    register,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  useEffect(() => {
    if (editValue && departmentLeadList.length > 0) {
      setValue("department", editDepartmentDetails.department_name);

      const leadMember = departmentLeadList.find(
        (m) => m.username === editDepartmentDetails.department_lead_name
      );
      const proxyMember = departmentLeadList.find(
        (m) => m.username === editDepartmentDetails.proxy_name
      );

      setValue("departmentLead", leadMember?.username ?? "");
      setValue("departmentLeadId", leadMember?.id ?? null);  
      setValue("proxyName", proxyMember?.username ?? "");
      setValue("departmentProxy", proxyMember?.id ?? null);  

      setDepartmentLeadName(leadMember?.username ?? "");
      setDepartmentProxyName(proxyMember?.username ?? "");

      setEditValue(false);
    }
  }, [editDepartmentDetails, editValue, setValue, departmentLeadList]);

  const onSubmit = async (data) => {
    setButtonLoader(true);
    if (data) {
      const response = await customAPI(`${domain}edit_department/`, "PUT", {
        department_lead_id: data.departmentLeadId,
        departmentname: data.department.trim(),
        department_id: editDepartmentDetails.department_id,
        proxy_id: data.departmentProxy,
        approved_by: userDetails.username,
      });
      if (response.ok) {
        reset();
        setOpenEditScreen(false);
        setDepartmentApi(true);
        showToast("success", response?.responseDetails?.message);
      } else {
        setButtonLoader(false);
      }
    }
  };

  const checkSpecialChar = (e) => {
    if (!/^[a-zA-Z0-9 ]*$/.test(e.key)) {
      e.preventDefault();
    }
  };


  return (
    <React.Fragment>
      <Mui.Grid container sx={{ paddingTop: 1 }}>
        <BackArrow actionFunction={() => setOpenEditScreen(false)} />
        <Mui.Typography
          sx={{ fontWeight: "bold", fontSize: 25, paddingTop: 1 }}
        >
          Departments / Edit Department
        </Mui.Typography>
      </Mui.Grid>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Mui.Grid container sx={{ paddingTop: 2 }}>
          <Mui.Grid xs={12} sm={12} md={3} lg={2.5}></Mui.Grid>
          <Mui.Grid xs={12} sm={12} md={6} lg={7.5} sx={{ paddingTop: 10 }}>
            <Mui.Grid container component={Mui.Paper}>
              <Mui.Grid
                xs={12}
                sx={{
                  padding: 1,
                  backgroundColor: themes.primary,
                  borderRadius: "10px 10px 0 0",
                  border: `2px solid ${themes.sidebarDivider}`,
                  borderBottom: "none",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Mui.Typography
                  sx={{ fontSize: 24, fontWeight: 800, font: "bold" }}
                >
                  Edit Department
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
            <Mui.Stack
              sx={{
                paddingTop: 1,
                padding: 5,
                borderRadius: "0 0 10px 10px",
                border: `2px solid ${themes.sidebarDivider}`,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Mui.Grid container sx={{ paddingTop: 1 }}>
                <Mui.Grid xs={4}>
                  <Mui.Typography sx={formKeyText}>
                    Department name&nbsp;
                    <Mui.Tooltip
                      arrow
                      slotProps={tooltipStyleProps}
                      title={
                        "Modify the department name if needed. Ensure it remains clear and distinct to avoid confusion across teams."
                      }
                    >
                      <Icons.Info />
                    </Mui.Tooltip>
                  </Mui.Typography>
                </Mui.Grid>
                <Mui.Grid xs={8}>
                  <Mui.TextField
                    size="small"
                    placeholder="Enter department name"
                    sx={{
                      borderWidth: "10px solid black",
                      paddingBottom: 2,
                    }}
                    {...register("department", {
                      required: "Department is required",
                      maxLength: {
                        value: 30,
                        message: "Maximum length is 30 characters",
                      },
                      validate: {
                        noDigitsOrSpecial: (fieldValue) => {
                          if (!/[A-Za-z]/.test(fieldValue)) {
                            return "Department Name must contain at least one alphabet character.";
                          }
                          if (/^\d+$/.test(fieldValue)) {
                            return "Department Name should not contain only digits.";
                          }
                          if (!/^[A-Za-z0-9\s&-]+$/.test(fieldValue)) {
                            return "Department name should only contain alphabets, spaces, -,numbers and & symbol.";
                          }
                          return true;
                        },
                        checkUnique: async (fieldValue) => {
                          if (
                            fieldValue !== "" &&
                            fieldValue !==
                              editDepartmentDetails.department_name &&
                            !buttonLoader
                          ) {
                            setButtonLoader(true);
                            try {
                              const response = await fetch(
                                `${domain}check_department_name/?department_name=${fieldValue.trim()}`,
                                {
                                  method: "GET",
                                  headers: {
                                    "content-type": "application/json",
                                    Authorization: `token ${token}`,
                                  },
                                }
                              );
                              const res = await response.json();
                              if (response.ok) {
                                if (res.exists) {
                                  return "The department name has been already taken.Use a unique department name to proceed further";
                                } else {
                                  return true;
                                }
                              } else if (response.status === 409) {
                                sessionHandling();
                              } else {
                                throw new Error('REQUEST_FAILED');
                              }
                            } catch (error) {
                              console.error('[Editdepartment] operation failed:', error);
                              showToast("error", "Couldn't save department details. Please try again.");
                            } finally {
                              setButtonLoader(false);
                            }
                          }
                        },
                      },
                    })}
                    onChange={(event) => {
                      setDepartmentName(event.target.value);
                      setValue("department", event.target.value);
                    }}
                    value={departmentName}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message || ""}
                    onKeyPress={checkSpecialChar}
                    InputProps={{
                      startAdornment: (
                        <Mui.InputAdornment>
                          <Mui.InputAdornment>
                            <Icons.AddBusiness
                              style={{
                                color: "#a6a6a6",
                              }}
                            />
                            &nbsp;
                          </Mui.InputAdornment>
                        </Mui.InputAdornment>
                      ),
                    }}
                    fullWidth
                    disabled={
                      editDepartmentDetails?.department_name === "General"
                        ? true
                        : false
                    }
                  />
                </Mui.Grid>
              </Mui.Grid>
              <Mui.Grid container sx={{ paddingTop: 1 }}>
                <Mui.Grid xs={4}>
                  <Mui.Typography sx={formKeyText}>
                    Department Lead&nbsp;
                    <Mui.Tooltip
                      arrow
                      slotProps={tooltipStyleProps}
                      title={
                        "Update the primary person responsible for managing the department.Useful in case of role changes or leadership shifts."
                      }
                    >
                      <Icons.Info />
                    </Mui.Tooltip>
                  </Mui.Typography>
                </Mui.Grid>
                <Mui.Grid xs={8}>
                  <Mui.Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    size="small"
                    value={
                      departmentLeadList
                        .map((obj) => ({ ...obj, label: obj.username }))
                        .find((opt) => opt.id === watch("departmentLeadId")) 
                      ?? 
                      departmentLeadList
                        .map((obj) => ({ ...obj, label: obj.username }))
                        .find((opt) => opt.username === editDepartmentDetails.department_lead_name)
                      ?? null
                    }
                    options={departmentLeadList.map((obj) => ({
                      ...obj,
                      label: obj.username,
                    }))}
                    sx={{ width: "100%" }}
                    ListboxProps={{ style: { maxHeight: "240px" } }}
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        option.label
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <img
                          src={option.image_url ?? optionalAvathar}
                          alt={option.label}
                          style={{
                            width: 25,
                            height: 25,
                            marginRight: 5,
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                          <span>{option.label}</span>
                          {option.employee_id ? (
                            <span style={{ fontSize: 12, color: "#6b7280" }}>
                              {option.employee_id}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    )}
                    onChange={(event, value) => {
                      if (value) {
                        setDepartmentLeadName(value.username);
                        setValue("departmentLeadId", value.id);
                        setValue("departmentLead", value.username);
                      } else {
                        setValue("departmentLeadId", null);
                        setDepartmentLeadName("");
                      }
                    }}
                    renderInput={(params) => (
                      <Mui.TextField
                        {...params}
                        value={departmentLeadName}
                        {...register("departmentLead", {
                          required: "Department lead is required",
                        })}
                        sx={{
                          background: themes.whiteColor,
                          maxHeight: 120,
                          paddingBottom: 2,
                        }}
                        placeholder="Select department lead"
                        size="small"
                        error={Boolean(errors.departmentLead)}
                        helperText={errors.departmentLead?.message || ""}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {departmentLeadName && (
                                <img
                                  src={
                                    departmentLeadList.find(
                                      (u) => u?.id === watch("departmentLeadId")
                                    )?.image_url ?? optionalAvathar
                                  }
                                  alt="avatar"
                                  style={{
                                    width: 25,
                                    height: 25,
                                    borderRadius: "50%",
                                    marginRight: 1,
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        fullWidth
                      />
                    )}
                  />
                </Mui.Grid>
              </Mui.Grid>
              <Mui.Grid container>
                <Mui.Grid xs={4}>
                  <Mui.Typography sx={formKeyText}>
                    Proxy&nbsp;
                    <Mui.Tooltip
                      arrow
                      slotProps={tooltipStyleProps}
                      title={
                        "Change the proxy (alternate lead) who can take action in the absence of the primary lead.This helps maintain continuity in approvals and operations."
                      }
                    >
                      <Icons.Info />
                    </Mui.Tooltip>
                  </Mui.Typography>
                </Mui.Grid>
                <Mui.Grid xs={8}>
                  <Mui.Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    size="small"
                    value={
                      departmentLeadList
                        .map((obj) => ({ ...obj, label: obj.username }))
                        .find((opt) => opt.id === watch("departmentProxy"))
                      ??
                      departmentLeadList
                        .map((obj) => ({ ...obj, label: obj.username }))
                        .find((opt) => opt.username === editDepartmentDetails.proxy_name)
                      ?? null
                    }
                    options={departmentLeadList.map((obj) => ({
                      ...obj,
                      label: obj.username,
                    }))}
                    sx={{ width: "100%", paddingBottom: 2 }}
                    ListboxProps={{ style: { maxHeight: "240px" } }}
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        option.label
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                    renderOption={(props, option) => {
                      return (
                        <li
                          {...props}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <img
                            src={option.image_url ?? optionalAvathar}
                            alt={option.username}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              marginRight: 5,
                              objectFit: "cover",
                            }}
                          />
                          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.25 }}>
                            <span>{option.username}</span>
                            {option.employee_id ? (
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                {option.employee_id}
                              </span>
                            ) : null}
                          </div>
                        </li>
                      );
                    }}
                    onChange={(event, value) => {
                      if (value) {
                        setDepartmentProxyName(value.username);
                        setValue("departmentProxy", value.id);
                        setValue("proxyName", value.username);
                      } else {
                        setValue("departmentProxy", null);
                        setDepartmentProxyName("");
                      }
                    }}
                    renderInput={(params) => (
                      <Mui.TextField
                        {...params}
                        value={departmentProxyName}
                        {...register("proxyName", {
                          required: "Proxy name is required",
                        })}
                        sx={{
                          background: themes.whiteColor,
                          maxHeight: 120,
                        }}
                        placeholder="Select proxy name"
                        size="small"
                        error={Boolean(errors.proxyName)}
                        helperText={errors.proxyName?.message || ""}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {departmentProxyName && (
                                <img
                                  src={
                                    departmentLeadList.find(
                                      (u) => u?.id === watch("departmentProxy")
                                    )?.image_url ?? optionalAvathar
                                  }
                                  alt="avatar"
                                  style={{
                                    width: 23,
                                    height: 23,
                                    borderRadius: "50%",
                                    marginRight: 1,
                                    objectFit: "cover",
                                  }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                        fullWidth
                      />
                    )}
                  />
                </Mui.Grid>
              </Mui.Grid>

              <Mui.Grid
                container
                sx={{ justifyContent: "right", paddingBottom: 2 }}
              >
                <CustomButton
                  key={buttonLoader}
                  actionName="Update"
                  typeName="submit"
                  disableAction={buttonLoader}
                />
              </Mui.Grid>
            </Mui.Stack>
          </Mui.Grid>
          <Mui.Grid xs={12} sm={12} md={3} lg={3}></Mui.Grid>
        </Mui.Grid>
      </form>
    </React.Fragment>
  );
};

function Tag(props) {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      <Close onClick={onDelete} />
    </div>
  );
}

Tag.propTypes = {
  label: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};
